/// <reference lib="webworker" />

import type { PrecacheEntry, SerwistGlobalConfig } from "serwist";
import {
  Serwist,
  CacheFirst,
  NetworkFirst,
  StaleWhileRevalidate,
  ExpirationPlugin,
} from "serwist";

declare global {
  interface ServiceWorkerGlobalScope extends SerwistGlobalConfig {
    __SW_MANIFEST: (PrecacheEntry | string)[] | undefined;
  }
}

declare const self: ServiceWorkerGlobalScope;

const serwist = new Serwist({
  precacheEntries: self.__SW_MANIFEST,
  skipWaiting: false,
  clientsClaim: true,
  navigationPreload: false,

  runtimeCaching: [
    {
      matcher: /^https:\/\/fonts\.(?:googleapis|gstatic)\.com\/.*/i,
      handler: new CacheFirst({
        cacheName: "google-fonts",
        plugins: [
          new ExpirationPlugin({
            maxEntries: 20,
            maxAgeSeconds: 365 * 24 * 60 * 60,
          }),
        ],
      }),
    },
    {
      matcher: /\/media\/icon/i,
      handler: new CacheFirst({
        cacheName: "tenant-icons",
        plugins: [
          new ExpirationPlugin({
            maxEntries: 20,
            maxAgeSeconds: 7 * 24 * 60 * 60,
          }),
        ],
      }),
    },
    {
      matcher: ({ url }: { url: URL }) =>
        url.hostname.endsWith("cloudfront.net") ||
        url.hostname === "firebasestorage.googleapis.com" ||
        url.hostname === "storage.googleapis.com",
      handler: new CacheFirst({
        cacheName: "cdn-assets",
        plugins: [
          new ExpirationPlugin({
            maxEntries: 60,
            maxAgeSeconds: 30 * 24 * 60 * 60,
          }),
        ],
      }),
    },
    {
      matcher: /\.(?:png|jpg|jpeg|svg|gif|webp|avif)$/i,
      handler: new CacheFirst({
        cacheName: "images-cache",
        plugins: [
          new ExpirationPlugin({
            maxEntries: 100,
            maxAgeSeconds: 30 * 24 * 60 * 60,
          }),
        ],
      }),
    },
    {
      matcher: /\/api\/public\/.*/i,
      handler: new NetworkFirst({
        cacheName: "api-public-cache",
        networkTimeoutSeconds: 6,
        plugins: [
          new ExpirationPlugin({
            maxEntries: 60,
            maxAgeSeconds: 5 * 60,
          }),
        ],
      }),
    },
    {
      matcher: ({ request }: { request: Request }) =>
        request.mode === "navigate",
      handler: new NetworkFirst({
        cacheName: "pages-cache",
        plugins: [
          new ExpirationPlugin({
            maxEntries: 50,
            maxAgeSeconds: 24 * 60 * 60,
          }),
        ],
      }),
    },
    {
      matcher: /\.(?:js|css)$/i,
      handler: new StaleWhileRevalidate({
        cacheName: "static-resources",
        plugins: [
          new ExpirationPlugin({
            maxEntries: 60,
            maxAgeSeconds: 30 * 24 * 60 * 60,
          }),
        ],
      }),
    },
  ],

  fallbacks: {
    entries: [
      {
        url: "/offline",
        matcher({ request }: { request: Request }) {
          return request.destination === "document";
        },
      },
    ],
  },
});

serwist.addEventListeners();

let firebaseScriptsLoaded = false;
let firebaseInitialized = false;

function loadFirebaseScripts(): boolean {
  if (firebaseScriptsLoaded) return true;

  try {
    importScripts(
      "https://www.gstatic.com/firebasejs/12.3.0/firebase-app-compat.js",
    );
    importScripts(
      "https://www.gstatic.com/firebasejs/12.3.0/firebase-messaging-compat.js",
    );
    firebaseScriptsLoaded = true;
    return true;
  } catch (error) {
    return false;
  }
}

loadFirebaseScripts();

declare const firebase: {
  initializeApp: (config: Record<string, string>) => void;
  messaging: () => {
    onBackgroundMessage: (
      callback: (payload: {
        notification?: { title?: string; body?: string };
        data?: Record<string, string>;
      }) => void,
    ) => void;
  };
};

function initializeFirebase(config: Record<string, string>): boolean {
  if (firebaseInitialized) return true;

  if (!firebaseScriptsLoaded && !loadFirebaseScripts()) {
    return false;
  }

  const requiredFields = [
    "apiKey",
    "authDomain",
    "projectId",
    "storageBucket",
    "messagingSenderId",
    "appId",
  ];

  const missingFields = requiredFields.filter((field) => !config[field]);
  if (missingFields.length > 0) {
    return false;
  }

  try {
    firebase.initializeApp(config);
    const messaging = firebase.messaging();

    messaging.onBackgroundMessage((payload) => {
      const notificationTitle = payload.notification?.title || "Frota360";
      const notificationOptions: NotificationOptions = {
        body: payload.notification?.body || "",
        icon: "/media/icon?size=192",
        badge: "/media/icon?size=72",
        tag: payload.data?.tag || "frota360-notification",
        data: payload.data || {},
        // @ts-expect-error — actions é suportado mas não está no tipo padrão
        actions: [
          { action: "view", title: "Ver detalhes" },
          { action: "dismiss", title: "Dispensar" },
        ],
        requireInteraction: payload.data?.requireInteraction === "true",
        vibrate: [200, 100, 200],
      };

      self.registration.showNotification(
        notificationTitle,
        notificationOptions,
      );
    });

    firebaseInitialized = true;
    return true;
  } catch (error) {
    console.error("[SW] Erro ao inicializar Firebase:", error);
    return false;
  }
}

self.addEventListener("message", async (event) => {
  const data = event.data;

  if (data?.type === "CACHE_TENANT_ASSETS" && Array.isArray(data.urls)) {
    const cache = await caches.open("cdn-assets");
    for (const url of data.urls as string[]) {
      try {
        const cached = await cache.match(url);
        if (!cached) {
          await cache.add(url);
        }
      } catch {
        // Silent
      }
    }
  }

  if (data?.type === "FIREBASE_CONFIG" && !firebaseInitialized) {
    const success = initializeFirebase(data.config);
    if (event.source && "postMessage" in event.source) {
      (event.source as Client).postMessage({
        type: "FIREBASE_INIT_RESULT",
        success,
      });
    }
  }

  if (data?.type === "SKIP_WAITING") {
    self.skipWaiting();
  }
});

self.addEventListener("notificationclick", (event) => {
  event.notification.close();

  const action = (event as NotificationEvent & { action?: string }).action;
  const data = (event.notification.data as Record<string, string>) || {};

  if (action === "dismiss") return;

  let targetUrl = "/admin";
  if (data.url) {
    targetUrl = data.url;
  } else if (data.type === "vehicle-alert" && data.vehicleId) {
    targetUrl = `/admin/vehicles/${data.vehicleId}`;
  } else if (data.type === "document-expiring" && data.vehicleId) {
    targetUrl = `/admin/vehicles/${data.vehicleId}?tab=documents`;
  } else if (data.type === "payment") {
    targetUrl = "/admin/payments";
  }

  event.waitUntil(
    self.clients
      .matchAll({ type: "window", includeUncontrolled: true })
      .then((windowClients) => {
        for (const client of windowClients) {
          if (client.url.includes(self.location.origin) && "focus" in client) {
            (client as WindowClient).navigate(targetUrl);
            return (client as WindowClient).focus();
          }
        }
        if (self.clients.openWindow) {
          return self.clients.openWindow(targetUrl);
        }
      }),
  );
});

self.addEventListener("push", (event) => {
  if (!event.data) return;

  try {
    const payload = event.data.json();

    if (payload.notification) return;

    if (payload.data) {
      const title = payload.data.title || "Frota360";
      const options: NotificationOptions = {
        body: payload.data.body || "",
        icon: "/media/icon?size=192",
        badge: "/media/icon?size=72",
        tag: payload.data.tag || "frota360-notification",
        data: payload.data,
      };

      event.waitUntil(self.registration.showNotification(title, options));
    }
  } catch (error) {}
});
